import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
} from '@floating-ui/dom';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, PropertyValueMap, css, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import TomSelect from 'tom-select';
import { escape_html } from 'tom-select/dist/types/utils';

import tomSelectMinified from '@/assets/css/tom-select.min.css?inline';
import CheckUrl from '@/assets/svg/check.svg?raw';
import StopwatchUrl from '@/assets/svg/stopwatch.svg?raw';
import { MobileController } from '@/controllers';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, rootVariablesStyles, tomSelectStyles } from '@/styles';
import { OptGroup, SelectOption, SelectVersion } from '@/types';
import { isIOS, sameWidth, svgUriEncoder } from '@/utils';

@customElement('fg-select')
@localized()
export class FgSelect extends LitElement {
  /**
   * If you enable autocomplete, you can type in the input and on default will suggest/autocomplete options matching the
   * input value.
   */
  @property({ type: Boolean })
  autocomplete = false;

  @property({ type: Boolean })
  translate = false;

  /**
   * Field in an option that groups the option in a specific group
   * See the description of the property optGroups to see what is necessary to make this property work
   */
  @property({ type: String })
  optGroupField?: string;

  /**
   * Option groups are used to group options based on a specific value provided in the option group. The label property represents the name of the option group.
   * To use option groups, you need to set the optGroupField property and ensure that the options have the optGroupField as a property.
   */
  @property({ type: Array })
  optGroups?: OptGroup[];

  /**
   * To preselect a value on load you need to pass one of the values form the options array. If none is given the
   * placeholder will be rendered.
   */
  @property({ type: String })
  defaultValue?: string;

  /**
   * What input version will be rendered, see: {@link SelectVersion} for all the available options.
   * @default SelectVersion.DEFAULT
   */
  @property({ type: String })
  version: SelectVersion = SelectVersion.DEFAULT;

  /**
   * The placeholder text.
   * @default 'Select a option'
   */
  @property({ type: String })
  placeholder = 'Select a option';

  /**
   * To render the amount of selected options in the multi select input field when selecting values. This has to be a
   * i18next translation key.
   */
  @property({ type: String })
  multiSelectTranslationKey = 'labels.selected';

  /**
   * The options that will be rendered in the select. The data is rendered based on the version property.
   */
  @property({ type: Array })
  options!: SelectOption[];

  /**
   * The max amount of options to display in the options dropdown
   */
  @property({ type: Number })
  maxOptions = 50;

  /**
   * This will set the dropdown min-width to 400px, if you have dropdown content that need more width then the input
   * element. You can set this property op give the dropdown a larger width.
   */
  @property({ type: Boolean })
  largeDropdown = false;

  /**
   * To enable positioning features and accurately position the dropdown,
   * you can activate the 'computePositioningEnabled' option.
   * This is particularly useful when positioning the dropdown within a dialog or similar components.
   */
  @property({ type: Boolean })
  computePositioningEnabled = false;

  /**
   * The current TomSelect instance. See docs for more info about the TomSelect library.
   * @docs {@link https://tom-select.js.org/docs}
   */
  @state()
  tomSelect?: TomSelect;

  /**
   * The amount of selected options in the multi select input field. This is used to render the custom label inside the
   * custom input element of multi select.
   */
  @state()
  multiSelectCount = 0;

  /**
   * This is used when the 'computePositioningEnabled' property is set to true to clean up the 'computePositioning' function.
   */
  @state()
  cleanUpPositioningFunction?: () => void;

  @query('[data-ts-item]')
  selectedItemElement?: HTMLElement;

  selectRef: Ref<HTMLSelectElement> = createRef();

  protected mobileController = new MobileController(this);

  setSelectedOption() {
    if (!this.options || this.version === SelectVersion.MULTI_SELECT) {
      return;
    }

    if (this.defaultValue || this.defaultValue === '') {
      this.tomSelect?.setValue(this.defaultValue);
      return;
    }
  }

  translateString(string: string) {
    return this.translate ? t(string) : string;
  }

  openDropdownWithPositioning = () => {
    if (!this.tomSelect) return;
    Object.assign(this.tomSelect.dropdown.style, {
      position: 'fixed',
    });
    this.cleanUpPositioningFunction = autoUpdate(
      this.tomSelect?.control,
      this.tomSelect?.dropdown,
      this.positionDropdown,
    );
  };

  positionDropdown = () => {
    if (!this.tomSelect?.control) return;
    const offsetValue = 6;
    computePosition(this.tomSelect?.control, this.tomSelect?.dropdown, {
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [offset(offsetValue), flip(), hide(), sameWidth],
    }).then(({ x, y, placement, middlewareData }) => {
      const { hide } = middlewareData;
      const { isMobile } = this.mobileController;

      if (!this.tomSelect?.dropdown) return;
      Object.assign(this.tomSelect.dropdown.style, {
        left: `${x}px`,
        top: `${placement === 'bottom-start' ? y - offsetValue : y}px`,
        ...(hide && {
          visibility:
            hide?.referenceHidden && !(isMobile && isIOS())
              ? 'hidden'
              : 'visible',
        }),
      });
    });
  };

  /**
   * How to render the options based on the version property.
   *
   * @param version {@link SelectVersion}
   * @param option {@link SelectOption}
   * @returns string - String literal with the option template html.
   */
  renderOptionsTemplate(
    version: SelectVersion,
    option: SelectOption,
    escape: typeof escape_html,
  ): any {
    switch (version) {
      case SelectVersion.SINGLE_SELECT:
        return `<div class="single-select ${!option.value ? 'default' : ''}">
            <section>
              <span class="radio"></span>
            </section>
            <section>
              <span class="text">
                ${this.translateString(escape(option.text))}
              </span>
              <span class="sub-text">
                ${
                  option['sub-text']
                    ? `${this.translateString(escape(option['sub-text']))}`
                    : ``
                }
              <span>
              ${
                option.hsp
                  ? `<span class="hsp-text">                          
                      ${t('labels.24hProductionAvailable')}
                    </span>`
                  : ``
              }
            </section>
          </div>`;
      case SelectVersion.MULTI_SELECT:
        return `<div class="multi-select">
          <section>
            <span class="checkbox"></span>
          </section>
          <section>
            <span class="text">
              ${this.translateString(option.text)}
            </span>
            <span class="sub-text">
              ${option['sub-text'] && `(${this.translateString(escape(option['sub-text']))})`}
            <span>
          </section>
        </div>`;
      case SelectVersion.COLOR_SELECT:
        return `
          <div class="color-select">
            ${
              option.visual
                ? `<section class="color">
                  <span style="background-color:${escape(
                    option.visual,
                  )};"></span>
                </section>`
                : null
            }
            <section>
              ${this.translateString(option.text)}
            </section>
          </div>`;
      case SelectVersion.FONT_SELECT:
        return `<div style="font-family: '${
          option.font ? escape(option.font) : ''
        }'">
        ${this.translateString(escape(option.text))}
        </div>`;
      default:
        return `<div>${this.translateString(escape(option.text))}</div>`;
    }
  }

  protected firstUpdated() {
    if (!this.selectRef.value || this.options === undefined) {
      return;
    }

    this.tomSelect?.off('change');

    const plugins =
      this.version === SelectVersion.MULTI_SELECT
        ? {
            checkbox_options: {
              checkedClassNames: ['ts-checked'],
              uncheckedClassNames: ['ts-unchecked'],
            },
          }
        : {};

    const editOption:
      | { controlInput: string | HTMLInputElement | undefined }
      | object = this.autocomplete ? {} : { controlInput: null as any };

    this.tomSelect = new TomSelect(this.selectRef.value, {
      ...editOption,
      placeholder: this.placeholder,
      options: this.options,
      ...(this.optGroups && { optgroups: this.optGroups }),
      ...(this.optGroupField && { optgroupField: this.optGroupField }),
      maxOptions: this.maxOptions,
      hideSelected:
        this.version !== SelectVersion.MULTI_SELECT &&
        this.version !== SelectVersion.SINGLE_SELECT,
      render: {
        item: (data: SelectOption, escape: typeof escape_html) => {
          return `<div>
              ${
                this.version === SelectVersion.COLOR_SELECT && data.visual
                  ? `<span class="color" style="background-color:${escape(
                      data.visual,
                    )};"></span>`
                  : ''
              }
              ${this.translateString(data.text)}
            </div>`;
        },
        option: (data: SelectOption, escape: typeof escape_html) => {
          return this.renderOptionsTemplate(this.version, data, escape);
        },
        optgroup_header: (data: OptGroup, escape: typeof escape_html) => {
          if (!data?.label) {
            return '';
          }
          return `<label class='optgroup-header'>${escape(data.label)}:</label>`;
        },
      },
      plugins: plugins,
    });

    this.setSelectedOption();
    this.onChangeEventTrigger();

    if (!this.computePositioningEnabled) return;

    const { isMobile } = this.mobileController;

    if (isMobile && isIOS()) {
      this.tomSelect?.on('focus', () => {
        Object.assign(document.body.style, {
          position: 'fixed',
          height: '100dvh',
        });
      });
      this.tomSelect?.on('blur', () => {
        Object.assign(document.body.style, {
          position: 'relative',
          height: '100%',
        });
      });
    }

    this.tomSelect?.on('dropdown_open', this.openDropdownWithPositioning);
    this.tomSelect?.on('dropdown_close', () => {
      this.cleanUpPositioningFunction?.();
    });
    this.tomSelect?.on('type', (searchText: string) => {
      if (!this.selectedItemElement) return;

      Object.assign(this.selectedItemElement.style, {
        display: searchText?.length > 0 ? 'none' : '',
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.computePositioningEnabled) return;
    this.tomSelect?.off('dropdown_open');
    this.tomSelect?.off('dropdown_close');
    this.tomSelect?.off('type');

    const { isMobile } = this.mobileController;

    if (isMobile && isIOS()) {
      this.tomSelect?.off('focus');
      this.tomSelect?.off('blur');
    }
  }

  /**
   * On change of the TomSelect input field, this event will be triggered. It will track the selected options count if
   * the version is multi-select. It will also dispatch a custom event with the selected options value as a string or
   * a array of strings.
   */
  onChangeEventTrigger(): void {
    this.tomSelect?.on('change', (value: string[] | string) => {
      this.tomSelect?.blur();
      if (this.version === SelectVersion.MULTI_SELECT) {
        this.multiSelectCount = value.length;
      }

      this.dispatchEvent(
        new CustomEvent('options-selected', {
          detail: {
            value: value,
          },
        }),
      );
    });
  }

  protected willUpdate(_changedProperties: PropertyValueMap<FgSelect>): void {
    if (_changedProperties.has('options') && this.tomSelect && this.options) {
      this.tomSelect?.off('change');

      this.tomSelect.removeItem(this.defaultValue);
      this.tomSelect.clearOptions();
      this.tomSelect.addOption(this.options);

      this.setSelectedOption();
      this.onChangeEventTrigger();
    }

    if (
      _changedProperties.has('optGroups') &&
      this.tomSelect &&
      this.optGroups
    ) {
      this.tomSelect.clearOptionGroups();
      for (const optGroup of this.optGroups) {
        if (optGroup.value) {
          this.tomSelect.addOptionGroup(optGroup.value.toString(), optGroup);
        }
      }
    }
  }

  render() {
    if (!this.options) {
      return;
    }

    return html`
      <div
        class="select-container ${classMap({
          'large-dropdown': this.largeDropdown,
        })}"
      >
        ${this.version === SelectVersion.MULTI_SELECT &&
        this.multiSelectCount > 0
          ? html`
              <label class="multi-select-counter">
                ${t(this.multiSelectTranslationKey, {
                  count: this.multiSelectCount,
                })}
              </label>
            `
          : this.version === SelectVersion.MULTI_SELECT
            ? html`
                <label class="multi-select-placeholder">
                  ${t(this.placeholder)}
                </label>
              `
            : null}
        <select
          ?multiple=${this.version === SelectVersion.MULTI_SELECT}
          ${ref(this.selectRef)}
        ></select>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    tomSelectStyles,
    unsafeCSS(tomSelectMinified),
    css`
      .select-container {
        display: block;
        position: relative;
        max-height: 48px;
      }

      .ts-control {
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        z-index: 0;
      }

      .ts-dropdown {
        padding: 12px;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        border: none;
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
        z-index: 11;
      }

      .ts-dropdown .option {
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        color: var(--fg-text, var(--fg-fallback-text));
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
      }

      .multi-select .tomselect-checkbox {
        display: none;
      }

      .ts-dropdown .option.multi-select,
      .ts-dropdown .option.single-select {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border: 1px solid transparent;
      }
      .select-container:has(.color-select) {
        max-height: 100%;
      }

      .ts-dropdown:has(.color-select) .color {
        margin-right: 5px;
      }

      .ts-dropdown:has(.color-select) .ts-dropdown-content {
        gap: 0px;
        max-height: 250px;
        scroll-behavior: auto;

        @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX}px) {
          & {
            max-height: 180px;
          }
        }
      }

      .ts-dropdown:has(.color-select) {
        padding: 0px;
        line-height: 100%;
        overflow-y: scroll;
      }

      .ts-dropdown:has(.color-select) .optgroup:first-of-type {
        margin-top: 8px;
      }

      .ts-dropdown:has(.color-select) .optgroup {
        margin-bottom: 3px;
      }

      .ts-dropdown:has(.color-select) .highlight {
        font-size: 16px;
      }

      .ts-dropdown:has(.color-select) .optgroup-header {
        padding: 0px 8px;
        background-color: transparent;
        font-size: 12px;
      }

      .ts-dropdown:has(.color-select) .optgroup-header .highlight {
        font-size: 12px;
      }

      .ts-dropdown .option.color-select {
        display: flex;
        align-items: center;
        background-color: transparent;
        border-radius: 0px;
        padding: 4px 8px;
      }

      .select-container:has(.color-select) .ts-control > input {
        min-width: 0px;
      }

      .ts-dropdown .option.color-select.active {
        background-color: var(
          --fg-tertiary--light,
          var(--fg-fallback-tertiary--light)
        );
      }

      .multi-select .text {
        color: var(--fg-gray--900, var(--fg-fallback-gray--900));
        font-weight: 600;
      }

      .multi-select .sub-text {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
      }

      .multi .ts-control {
        cursor: pointer;
      }

      .multi .ts-control .item {
        display: none;
      }

      .ts-control .item {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .multi-select-counter,
      .multi-select-placeholder {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
      }

      .ts-dropdown .option.multi-select.selected,
      .ts-dropdown .option.single-select.selected {
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
        border-color: var(--fg-success, var(--fg-fallback-success));
      }

      :host([part='retail']) .ts-dropdown .option.multi-select.selected,
      :host([part='retail']) .ts-dropdown .option.single-select.selected {
        background-color: var(
          --fg-primary--light,
          var(--fg-fallback-primary--light)
        );
        border: none;
      }

      .color-select .color span,
      .ts-control .item .color {
        width: 20px;
        height: 20px;
        display: block;
      }

      .single-select .radio,
      .multi-select .checkbox {
        display: block;
        width: 24px;
        height: 24px;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        background-color: #fff;
      }

      .multi-select .checkbox,
      .color-select .color span,
      .ts-control .item .color {
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
      }

      .single-select.default {
        order: -1;
      }

      .single-select .radio {
        border-radius: 50%;
      }

      .single-select section:nth-of-type(2) {
        display: flex;
        flex-direction: column;
      }

      .single-select section:nth-of-type(2) .text {
        font-weight: 600;
      }

      .single-select section:nth-of-type(2) .sub-text {
        font-size: 14px;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
      }

      .multi-select.selected .checkbox,
      .single-select.selected .radio {
        border-color: var(--fg-success);
        background-color: var(--fg-success);
        background-image: url(${unsafeCSS(svgUriEncoder(CheckUrl))});
        background-repeat: no-repeat;
        background-position: center;
        background-size: 16px 16px;
      }

      .hsp-text {
        background-image: url(${unsafeCSS(svgUriEncoder(StopwatchUrl))});
        background-position: 0 center;
        background-repeat: no-repeat;
        display: flex;
        font-style: italic;
        padding-left: 1.5rem;
        width: 100%;
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
      }

      .optgroup span {
        font-size: 0.75rem;
      }

      .ts-dropdown .optgroup:not(:first-of-type):before {
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        content: '';
        display: block;
        height: 0;
        overflow: hidden;
        margin-bottom: 4px;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .large-dropdown .ts-wrapper .ts-dropdown {
          min-width: 400px;
        }
      }
    `,
  ];
}
